import { createContext, useContext, useEffect, useState, useCallback } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type BrandTheme = "default" | "claude" | "chatgpt" | "custom";

export interface CustomColors {
  accent: string;
  accentHover: string;
  sidebar: string;
  surface: string;
  sidebarDark: string;
  surfaceDark: string;
}

const DEFAULT_CUSTOM_COLORS: CustomColors = {
  accent: "#10a37f",
  accentHover: "#0d8a6a",
  sidebar: "#fafaf8",
  surface: "#fafaf8",
  sidebarDark: "#171717",
  surfaceDark: "#2f2f2f",
};

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  brand: BrandTheme;
  setBrand: (brand: BrandTheme) => void;
  customColors: CustomColors;
  setCustomColors: (colors: CustomColors) => void;
  resolvedTheme: "light" | "dark";
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_KEY = "proclaw-theme";
const BRAND_KEY = "proclaw-brand";
const CUSTOM_COLORS_KEY = "proclaw-custom-colors";

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function loadCustomColors(): CustomColors {
  try {
    const stored = localStorage.getItem(CUSTOM_COLORS_KEY);
    if (stored) return { ...DEFAULT_CUSTOM_COLORS, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return DEFAULT_CUSTOM_COLORS;
}

function applyCustomColors(colors: CustomColors) {
  const root = document.documentElement;
  root.style.setProperty("--custom-accent", colors.accent);
  root.style.setProperty("--custom-accent-hover", colors.accentHover);
  root.style.setProperty("--custom-sidebar", colors.sidebar);
  root.style.setProperty("--custom-surface", colors.surface);
  root.style.setProperty("--custom-sidebar-dark", colors.sidebarDark);
  root.style.setProperty("--custom-surface-dark", colors.surfaceDark);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(THEME_KEY);
    return (stored as ThemeMode) ?? "system";
  });

  const [brand, setBrandState] = useState<BrandTheme>(() => {
    const stored = localStorage.getItem(BRAND_KEY);
    return (stored as BrandTheme) ?? "default";
  });

  const [customColors, setCustomColorsState] = useState<CustomColors>(loadCustomColors);

  const resolved = theme === "system" ? getSystemTheme() : theme;

  const setTheme = useCallback((t: ThemeMode) => {
    setThemeState(t);
    localStorage.setItem(THEME_KEY, t);
  }, []);

  const setBrand = useCallback((b: BrandTheme) => {
    setBrandState(b);
    localStorage.setItem(BRAND_KEY, b);
  }, []);

  const setCustomColors = useCallback((c: CustomColors) => {
    setCustomColorsState(c);
    localStorage.setItem(CUSTOM_COLORS_KEY, JSON.stringify(c));
    applyCustomColors(c);
  }, []);

  // Apply dark class
  useEffect(() => {
    const r = theme === "system" ? getSystemTheme() : theme;
    document.documentElement.classList.toggle("dark", r === "dark");
  }, [theme]);

  // Apply brand attribute
  useEffect(() => {
    const root = document.documentElement;
    if (brand === "default") {
      root.removeAttribute("data-brand");
    } else {
      root.setAttribute("data-brand", brand);
    }
    if (brand === "custom") {
      applyCustomColors(customColors);
    }
  }, [brand, customColors]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      document.documentElement.classList.toggle("dark", mq.matches);
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{ theme, setTheme, brand, setBrand, customColors, setCustomColors, resolvedTheme: resolved }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
